'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useUser } from '@/features/auth/model/auth.selectors';
import { useJobQueueStore } from '@/features/design/job-queue';
import { appRatingService } from '../api/app-rating.service';
import { useAppRatingStore } from '../model/app-rating.store';
import { AppRatingDialog } from './AppRatingDialog';

/**
 * Delay between a finished render and the survey. The render is what the user
 * has been waiting for; a dialog on top of it gets dismissed unread. Same 5
 * seconds AppReviewManager waits on Apple and Android.
 */
const DELAY_AFTER_RENDER_MS = 5_000;

/**
 * Fallback delay for a session with no render at all.
 *
 * This path exists because of who it catches. A user who stopped generating is
 * exactly the user whose opinion we are missing, and a survey that only fires
 * after a successful render can never reach them: it selects for people still
 * happily working. Long enough that it never lands during the first glance at
 * the screen.
 */
const DELAY_WITHOUT_RENDER_MS = 60_000;

/**
 * `?app-rating=1` opens the survey immediately, skipping the server gate and
 * the delay. Without it the dialog is unreachable on demand: it needs two
 * finished renders and, once answered, hides for months — so every look at it
 * would otherwise cost a database edit.
 *
 * A value of 1-5 pre-selects that score (`?app-rating=2` lands straight on the
 * low-score branch with its reason chips, the half worth reviewing). Anything
 * else opens the dialog blank.
 */
const PREVIEW_PARAM = 'app-rating';

/**
 * Who may use the preview.
 *
 * Non-production builds always, plus `is_test` accounts anywhere — that is the
 * existing internal-QA marker on the profile, so the dialog can be checked on
 * a real production build without exposing it to customers. Note that local
 * runs are production BUILDS but not the production ENV, which is why this
 * reads `NEXT_PUBLIC_APP_ENV` (same signal Clarity and Firebase gate on) and
 * not `NODE_ENV`.
 */
function canPreview(isTestUser: boolean): boolean {
  return process.env.NEXT_PUBLIC_APP_ENV !== 'production' || isTestUser;
}

/**
 * Presents the satisfaction survey when the server says the user is due.
 *
 * The decision of WHETHER to ask is entirely the server's
 * (`should_request_feedback` on /user — completed-render threshold plus the
 * cooldown written by the previous answer). This component only owns WHEN
 * inside the session, and it holds no durable state: everything it remembers
 * dies with the tab, and both outcomes are reported to the API.
 */
export function AppRatingAutoStart() {
  const user = useUser();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const [answered, setAnswered] = useState(false);

  const lastRenderCompletedAt = useAppRatingStore(
    (state) => state.lastRenderCompletedAt
  );
  const offeredThisSession = useAppRatingStore(
    (state) => state.offeredThisSession
  );
  const markOffered = useAppRatingStore((state) => state.markOffered);
  const activeJobCount = useJobQueueStore((state) => state.jobQueue.length);

  const previewValue = searchParams.get(PREVIEW_PARAM);
  const isPreview =
    previewValue !== null && canPreview(user?.is_test === true);
  const previewRating =
    isPreview && /^[1-5]$/.test(previewValue) ? Number(previewValue) : null;

  // Equality check (not `!== false`): the field is absent until /user
  // populates the profile, and an absent flag must not read as "due".
  const isDue = user?.should_request_feedback === true;

  useEffect(() => {
    if (isPreview) {
      setOpen(true);
      return;
    }

    if (!isDue || offeredThisSession || answered) return;

    // Never interrupt a generation in flight — the user is watching a progress
    // bar, and the answer we would get is about the wait, not the product.
    if (activeJobCount > 0) return;

    const delay =
      lastRenderCompletedAt === null
        ? DELAY_WITHOUT_RENDER_MS
        : DELAY_AFTER_RENDER_MS;

    const timer = setTimeout(() => {
      setOpen(true);
      markOffered();
    }, delay);

    return () => clearTimeout(timer);
  }, [
    isPreview,
    isDue,
    offeredThisSession,
    answered,
    activeJobCount,
    lastRenderCompletedAt,
    markOffered,
  ]);

  if (!isDue && !isPreview) return null;

  return (
    <AppRatingDialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        // Closing without an answer is "Later": it counts as a refusal and
        // pushes the next prompt out a week. Unreported, the server would
        // offer the survey again on the next page load.
        //
        // The preview is exempt — looking at the dialog must not burn a
        // refusal on the account being tested.
        if (!value && !answered && !isPreview) void appRatingService.snooze();
      }}
      onAnswered={() => setAnswered(true)}
      initialRating={previewRating}
    />
  );
}
